import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    InputLabel,
    Switch,
    Divider,
    Chip,
    Stack,
    TextField,
    Alert,
    Card,
    CardContent,
    LinearProgress
} from "@mui/material";

import type {
    ArtworkReportRequest, ReportSelectionRequest, ArtworkReportType,
    ArtworkReportGroupBy, ArtworkReportSortBy, ArtworkReportFormat,
    ReportValuationFormat,
} from "../../models/Reporting";
import type { CurrentSearchSummary, ValuationType } from "../../models/SearchElements";
import CurrentSearchSummaryChips from "../Filter/CurrentSearchSummaryChips";
import { useUser } from '../../components/UserContext';

type ReportSelectionDialogProps = {
    open: boolean;
    onClose: () => void;
    onGenerate: (request: ReportSelectionRequest) => void;
    currentSearch?: CurrentSearchSummary;
    defaultTitle?: string;
    generating?: boolean;
};




export const ReportSelectionDialog: React.FC<ReportSelectionDialogProps> = ({
    open,
    onClose,
    onGenerate,
    currentSearch,
    defaultTitle,
    generating,
}) => {

    const { isFinance, dimensionDisplayPreference, dimensionUnitPreference } = useUser();

    const DEFAULT_REQUEST: ReportSelectionRequest = {
        title: "Artwork Valuation Report",
        reportType: "Insurance",
        groupBy: "Location",
        subGroupBy: "Category",
        sortBy: "Artist",
        reportFormat: "Pdf",
        reportValuationType: "insurance",
        reportValuationFormat: "standard",
        includeCoverPage: true,
        includeIndexPage: false,
        groupIndexPage: false,
        includeImages: true,
        includeDimensions: true,
        includeWeight: true,
        includeLocation: true,
        includeValuation: true,
        includeReference: true,
        includeMedium: true,
        includeProvenance: true,
        dimensionUnits: dimensionUnitPreference,
        dimensionDetails: dimensionDisplayPreference,
    };

    const [request, setRequest] = useState<ReportSelectionRequest>({
        ...DEFAULT_REQUEST,
        title: defaultTitle ?? DEFAULT_REQUEST.title
    });

    useEffect(() => {
        if (open) {
            setRequest({
                ...DEFAULT_REQUEST,
                title: defaultTitle ?? DEFAULT_REQUEST.title
            });
        }
    }, [open, defaultTitle]);

    const groupByOptions = [
        { value: "None", label: "None" },
        { value: "Artist", label: "Artist" },
        { value: "Category", label: "Category" },
        { value: "Location", label: "Location" },
    ];

    const subGroupByOptions = useMemo(
        () =>
            groupByOptions.filter(
                x => x.value === "None" || x.value !== request.groupBy
            ),
        [request.groupBy]
    );



    const isCatalogue = request.reportType === "Catalogue";
    const isDetail = request.reportType === "Detail";
    const isSummary = request.reportType === "Summary";
    const isInsurance = request.reportType === "Insurance";

    useEffect(() => {
        // Apply defaults when report type changes
        setRequest(prev => {
            if (prev.reportType === "Catalogue") {
                return {
                    ...prev,
                    title: "Catalogue Report",
                    groupBy: prev.groupBy === "None" ? "Category" : prev.groupBy,
                    subGroupBy: "None",
                    includeCoverPage: true,
                    includeIndexPage: false,
                    groupIndexPage: false,
                    includeImages: true,
                    includeDimensions: true,
                    includeWeight: true,
                    includeLocation: true,
                    includeReference: true,
                    includeMedium: true,
                    includeProvenance: false,
                    includeValuation: false,
                    reportValuationFormat: "compact",
                };
            }

            if (prev.reportType === "Insurance") {
                return {
                    ...prev,
                    title: "Artwork Valuation Report",
                    groupBy: "Location",
                    subGroupBy: "Category",
                    includeCoverPage: true,
                    includeIndexPage: false,
                    groupIndexPage: false,
                    includeImages: true,
                    includeDimensions: true,
                    includeWeight: true,
                    includeLocation: true,
                    includeReference: true,
                    includeValuation: true,
                    reportValuationType: "insurance",
                    includeMedium: true,
                    includeProvenance: false,
                    reportValuationFormat: "standard",
                };
            }

            if (prev.reportType === "Detail") {
                return {
                    ...prev,
                    title: "Artwork Detail Report",
                    subGroupBy: "None",
                    includeCoverPage: false,
                    includeIndexPage: true,
                    groupIndexPage: false,
                    includeImages: true,
                    includeDimensions: true,
                    includeWeight: true,
                    includeLocation: true,
                    includeReference: true,
                    includeValuation: true,
                    includeMedium: true,
                    includeProvenance: true,
                    reportValuationFormat: "standard",
                };
            }

            if (prev.reportType === "Summary") {
                return {
                    ...prev,
                    title: "Artwork Summary Report",
                    subGroupBy: "None",
                    includeCoverPage: false,
                    includeIndexPage: false,
                    groupIndexPage: false,
                    includeImages: false,
                    includeDimensions: false,
                    includeWeight: false,
                    includeLocation: false,
                    includeReference: true,
                    includeValuation: true,
                    includeMedium: false,
                    includeProvenance: false,
                    reportValuationFormat: "standard",
                };
            }

            return {
                ...prev,
                groupBy: "None",
                subGroupBy: "None",
                includeCoverPage: false,
                includeImages: false,
                includeWeight: false
            };
        });
    }, [request.reportType]);

    const summaryLines = useMemo(() => {
        const lines: string[] = [];

        if (currentSearch?.compactDescription) {
            lines.push(currentSearch.compactDescription);
            return lines;
        }

        if (currentSearch?.searchText) {
            lines.push(`Search: ${currentSearch.searchText}`);
        }

        if (currentSearch?.portfolioName) {
            lines.push(`Portfolio: ${currentSearch.portfolioName}`);
        }

        if (currentSearch?.artistNames?.length) {
            lines.push(`Artists: ${currentSearch.artistNames.join(", ")}`);
        }

        if (currentSearch?.categoryNames?.length) {
            lines.push(`Categories: ${currentSearch.categoryNames.join(", ")}`);
        }

        if (currentSearch?.locationNames?.length) {
            lines.push(`Locations: ${currentSearch.locationNames.join(", ")}`);
        }

        if (
            currentSearch?.minValue !== undefined ||
            currentSearch?.maxValue !== undefined
        ) {
            const currency = currentSearch.currency ?? "";
            const min = currentSearch.minValue !== undefined ? `${currency}${currentSearch.minValue}` : "Any";
            const max = currentSearch.maxValue !== undefined ? `${currency}${currentSearch.maxValue}` : "Any";
            lines.push(`Value range: ${min} to ${max}`);
        }

        return lines;
    }, [currentSearch]);

    const handleSwitch =
        (field: keyof ReportSelectionRequest) =>
            (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                setRequest(prev => ({ ...prev, [field]: checked }));
            };

    const handleGroupByChange = (value: ArtworkReportGroupBy) => {
        setRequest(prev => ({
            ...prev,
            groupBy: value,
            subGroupBy:
                value === "None" || prev.subGroupBy === value
                    ? "None"
                    : prev.subGroupBy
        }));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>Configure Report</DialogTitle>

            <DialogContent dividers>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1.3fr 0.9fr" }} gap={3}>
                    <Box>
                        <Stack spacing={2}>
                            <TextField
                                label="Report Title"
                                fullWidth
                                value={request.title ?? ""}
                                onChange={(e) =>
                                    setRequest(prev => ({ ...prev, title: e.target.value }))
                                }
                            />

                            <Card variant="outlined">
                                <CardContent>    
                                    <FormControl>
                                <FormLabel>Report Type</FormLabel>
                                <RadioGroup
                                    value={request.reportType}
                                    onChange={(e) =>
                                        setRequest(prev => ({
                                            ...prev,
                                            reportType: e.target.value as ArtworkReportType
                                        }))
                                    }
                                >
                                    <FormControlLabel
                                        value="Insurance"
                                        control={<Radio />}
                                        label="Valuation Report"
                                    />
                                    <FormControlLabel
                                        value="Catalogue"
                                        control={<Radio />}
                                        label="Catalogue"
                                    />
                                    <FormControlLabel
                                        value="Detail"
                                        control={<Radio />}
                                        label="Detail Report"
                                    />
                                    <FormControlLabel
                                        value="Summary"
                                        control={<Radio />}
                                        label="Summary Report"
                                    />
                                </RadioGroup>
                            </FormControl>
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>   
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                                <FormControl fullWidth>
                                    <InputLabel id="group-by-label">Group By</InputLabel>
                                    <Select
                                        labelId="group-by-label"
                                        label="Group By"
                                        value={request.groupBy}
                                        onChange={(e) => handleGroupByChange(e.target.value as ArtworkReportGroupBy)}
                                    >
                                        {groupByOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl>
                                    <InputLabel id="subgroup-by-label">Sub-Group By</InputLabel>
                                    <Select
                                        labelId="subgroup-by-label"
                                        label="Sub-Group By"
                                        value={request.subGroupBy}
                                        disabled={request.groupBy === "None" || !isInsurance}
                                        onChange={(e) =>
                                            setRequest(prev => ({
                                                ...prev,
                                                subGroupBy: e.target.value as ArtworkReportGroupBy,
                                            }))
                                        }
                                    >
                                        {subGroupByOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl fullWidth>
                                    <InputLabel id="sort-by-label">Sort By</InputLabel>
                                    <Select
                                        labelId="sort-by-label"
                                        label="Sort By"
                                        value={request.sortBy}
                                        onChange={(e) =>
                                            setRequest(prev => ({
                                                ...prev,
                                                sortBy: e.target.value as ArtworkReportSortBy
                                            }))
                                        }
                                    >
                                        <MenuItem value="Reference">Reference</MenuItem>
                                        <MenuItem value="Artist">Artist</MenuItem>
                                        <MenuItem value="Title">Title</MenuItem>
                                        <MenuItem value="Year">Year</MenuItem>
                                        <MenuItem value="Category">Category</MenuItem>
                                        <MenuItem value="Location">Location</MenuItem>
                                        (isFinance && <MenuItem value="Value">Value</MenuItem>)
                                    </Select>
                                </FormControl>
                            </Box>
                                </CardContent>
                            </Card>
                            <Divider />

                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Content Options
                                </Typography>

                                <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={1}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeCoverPage}
                                                onChange={handleSwitch("includeCoverPage")}
                                                disabled={false}
                                            />
                                        }
                                        label="Include cover page"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeImages}
                                                onChange={handleSwitch("includeImages")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include images"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeReference}
                                                onChange={handleSwitch("includeReference")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include references"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeMedium}
                                                onChange={handleSwitch("includeMedium")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include medium"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeDimensions}
                                                onChange={handleSwitch("includeDimensions")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include dimensions"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeWeight}
                                                onChange={handleSwitch("includeWeight")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include weight"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeLocation}
                                                onChange={handleSwitch("includeLocation")}
                                                disabled={isSummary}
                                            />
                                        }
                                        label="Include location"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeProvenance}
                                                onChange={handleSwitch("includeProvenance")}
                                                disabled={isSummary || isCatalogue}
                                            />
                                        }
                                        label="Include provenance"
                                    />

                                    {(isFinance &&
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={request.includeValuation}
                                                    onChange={handleSwitch("includeValuation")}
                                                    disabled={isCatalogue ? false : false}
                                                />
                                            }
                                            label="Include valuation"
                                        />)}

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.includeIndexPage}
                                                onChange={handleSwitch("includeIndexPage")}
                                                disabled={request.reportType !== "Detail"}
                                            />
                                        }
                                        label="Include index page"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={request.groupIndexPage}
                                                onChange={handleSwitch("groupIndexPage")}
                                                disabled={request.reportType !== "Detail" && !request.includeIndexPage}
                                            />
                                        }
                                        label="Group indexes"
                                    />
                                </Box>

                            </Box>

                            {isCatalogue && (
                                <Alert severity="info">
                                    Catalogue reports are designed for presentation similar to auction house catalogues, with multiple items listed per page.
                                </Alert>
                            )}

                            {isInsurance && (
                                <Alert severity="info">
                                    Valuation reports are designed for reporting items and their insurance (or other valuation), with a single item per row and values separated in a column.
                                </Alert>
                            )}

                            {isDetail && (
                                <Alert severity="info">
                                    Detail reports are suited to richer artwork information and include more details about each item, with one item per page.
                                </Alert>
                            )}

                            {isSummary && (
                                <Alert severity="info">
                                    Summary reports are a reference of selected artworks showing reference, artist, title and optionally value.
                                </Alert>
                            )}
                        </Stack>
                    </Box>

                    <Box>
                        <Stack spacing={2}>
                            <Card variant="outlined">
                                <CardContent>
                                    <CurrentSearchSummaryChips summary={currentSearch} />
                                </CardContent>
                            </Card>

                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                        Output Summary
                                    </Typography>

                                    <Stack spacing={1}>
                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Type:</strong>{" "}
                                            {request.reportType === "Catalogue"
                                                ? "Catalogue"
                                                : request.reportType === "Detail"
                                                    ? "Detail Report"
                                                    : "Summary Report"}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Grouping:</strong>{" "}
                                            {isSummary ? "None" : request.groupBy}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Sort:</strong> {request.sortBy}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                        <strong>Report Basis:</strong>{" "}
                                        {(request.includeValuation || request.sortBy == "Value") ? (
                                            <>
                                                {request.reportValuationType === "acquisition"
                                                    ? "Acquisition"
                                                    : request.reportValuationType === "insurance"
                                                        ? "Insurance"
                                                        : "Market Value"
                                                }
                                            </>) : (<>N/A</>)}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Format:</strong> {request.reportFormat}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            <strong>Included:</strong>{" "}
                                            {[
                                                request.includeImages && "images",
                                                request.includeReference && "references",
                                                request.includeMedium && "medium",
                                                request.includeDimensions && "dimensions",
                                                request.includeWeight && "weight",
                                                request.includeProvenance && "provenance",
                                                request.includeLocation && "location",
                                                request.includeValuation && "valuation",
                                                request.includeCoverPage && "cover page",
                                                request.includeIndexPage && "index page",
                                                request.groupIndexPage && "grouped indexes"
                                            ]
                                                .filter(Boolean)
                                                .join(", ") || "standard fields only"}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                            {(isFinance &&
                                <Card variant="outlined">
                                    <CardContent>
                                        <Stack spacing={1}>
                                        <FormControl fullWidth>
                                            <InputLabel id="valuation-type-label">Report Value Basis</InputLabel>
                                            <Select
                                                labelId="valuation-type-label"
                                                label="Report Value Basis"
                                                value={request.reportValuationType}
                                                disabled={!request.includeValuation && request.sortBy !== "Value"}
                                                onChange={(e) =>
                                                    setRequest(prev => ({
                                                        ...prev,
                                                        reportValuationType: e.target.value as ValuationType
                                                    }))
                                                }
                                            >
                                                <MenuItem value="acquisition">Acquisition</MenuItem>
                                                <MenuItem value="insurance">Insurance</MenuItem>
                                                <MenuItem value="fairMarketValue">Market Value</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <FormControl fullWidth>
                                            <InputLabel id="valuation-value-format">Report Value Format</InputLabel>
                                            <Select
                                                labelId="valuation-value-format"
                                                label="Report Value Format"
                                                value={request.reportValuationFormat}
                                                disabled={!request.includeValuation}
                                                onChange={(e) =>
                                                    setRequest(prev => ({
                                                        ...prev,
                                                        reportValuationFormat: e.target.value as ReportValuationFormat
                                                    }))
                                                }
                                            >
                                                <MenuItem value="standard">Standard</MenuItem>
                                                <MenuItem value="compact">Compact</MenuItem>
                                            </Select>
                                            </FormControl>
                                        </Stack>
                                    </CardContent>
                                </Card>)}
                            <Card variant="outlined">
                                <CardContent>
                                    <FormControl fullWidth size="small">
                                        <InputLabel id="report-format-label">Output Format</InputLabel>
                                        <Select
                                            labelId="report-format-label"
                                            label="Output Format"
                                            value={request.reportFormat ?? "Pdf"}
                                            onChange={(e) =>
                                                setRequest(prev => ({
                                                    ...prev,
                                                    reportFormat: e.target.value as ArtworkReportFormat
                                                }))
                                            }
                                        >
                                            <MenuItem value="Pdf">PDF</MenuItem>
                                            <MenuItem value="Excel">Excel</MenuItem>
                                        </Select>
                                    </FormControl>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={() => onGenerate(request)}
                    disabled={generating}
                >
                    {generating ? "Generating…" : "Generate Report"} {generating && <LinearProgress />}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportSelectionDialog;